import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, CircularProgress, Container, Grid2 as Grid, Typography,
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';

import Train from '../components/Train';
import { db } from '../firebase';
import { Context2 } from './Context2';
import { LangSwitch, useI18n } from '../i18n/I18nContext';
import StopRowPast from './StopRowPast';
import { CLOCK_TICK_MS, DerivedStop, deriveJourney } from './deriveJourney';
import { DISPLAY_TZ, delayLabel } from '../utils/liveTime';
import type { JourneyDoc, JourneyStatus, OccupancyDoc } from './types';

/**
 * The live2 board.
 *
 * Reads TWO documents, both keyed on journey_id, joined on stop_sequence:
 *   live_journeys/{id}           header + every stop, planned and observed times
 *   live_journey_occupancy/{id}  occupancy per stop, measured and forecast
 *
 * The occupancy document may be absent (no VU message yet) or ahead of the journey
 * document. Both are normal, and the empty case renders as NONE rather than a
 * spinner — a train with no counts is a legitimate thing to look at.
 *
 * Layout: past stops compact, current stop prominent, future stops full detail.
 *
 * NO LOAD PERCENTAGE anywhere. Occupancy is a passenger count plus a level. See
 * StopRowPast for the reasoning.
 */

const STATUS_STYLE: Record<JourneyStatus, { fg: string; bg: string }> = {
  ACTIVE: { fg: '#1b5e20', bg: '#e8f5e9' },
  ARRIVED: { fg: '#37474f', bg: '#eceff1' },
  SCHEDULED: { fg: '#37474f', bg: '#f5f5f5' },
  NO_DATA: { fg: '#b3261e', bg: '#fdecec' },
  CANCELLED: { fg: '#4a148c', bg: '#f3e5f5' },
};

const useClock = (intervalMs: number) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

const LiveView2: React.FC = () => {
  const { selected } = useContext(Context2);
  const { t, locale } = useI18n();

  const [journey, setJourney] = useState<JourneyDoc | null>(null);
  const [occupancy, setOccupancy] = useState<OccupancyDoc | null>(null);
  const [journeyLoaded, setJourneyLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(true);

  const now = useClock(1000);
  // Coarser tick for state derivation: flips AWAITING -> MISSING and moves the
  // current-stop split, neither of which produces a Firestore event.
  const [derivedNowMs, setDerivedNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setDerivedNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const currentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selected) {
      setJourney(null);
      setOccupancy(null);
      setJourneyLoaded(false);
      return;
    }

    const { journeyId } = selected;
    setJourneyLoaded(false);
    setError(null);

    const unsubJourney = onSnapshot(
      doc(db, 'live_journeys', journeyId),
      (snap) => {
        setJourney(snap.exists() ? ({ ...(snap.data() as JourneyDoc), journey_id: snap.id }) : null);
        setJourneyLoaded(true);
      },
      (err) => {
        console.error('❌ live_journeys doc onSnapshot error', err);
        setError(err.message);
        setJourneyLoaded(true);
      }
    );

    // Separate subscription: this document has a different writer on a different
    // clock (VU publishes ~35s after the station event), so it updates
    // independently and must not gate rendering of the journey.
    const unsubOccupancy = onSnapshot(
      doc(db, 'live_journey_occupancy', journeyId),
      (snap) => setOccupancy(snap.exists() ? (snap.data() as OccupancyDoc) : null),
      (err) => {
        console.error('❌ live_journey_occupancy doc onSnapshot error', err);
        setOccupancy(null);
      }
    );

    return () => {
      unsubJourney();
      unsubOccupancy();
    };
  }, [selected]);

  const derived = useMemo(
    () => deriveJourney(journey, occupancy, derivedNowMs),
    [journey, occupancy, derivedNowMs]
  );

  // Keep the current stop in view as the train progresses.
  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [derived?.currentSeq]);

  // Language changes formatting only — the timezone stays Berlin, because the board
  // reports Stuttgart operations whoever is reading it.
  const timeString = now.toLocaleTimeString(locale, {
    timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const dateString = now.toLocaleDateString(locale, {
    timeZone: DISPLAY_TZ, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const topBar = (
    <Box sx={{ width: 710, margin: '0 auto 12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <LangSwitch />
      <Box sx={{ flexGrow: 1 }} />
      <Typography fontSize={13} color="#666">
        {dateString} · {timeString} <span style={{ color: '#aaa' }}>{t('view.localTime')}</span>
      </Typography>
    </Box>
  );

  const shell = (children: React.ReactNode) => (
    <>
      {topBar}
      <Container
        sx={{
          height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {children}
      </Container>
    </>
  );

  if (!selected) return shell(<Typography fontSize={15} color="#888">{t('view.noSelection')}</Typography>);
  if (!journeyLoaded) return shell(
    <Box textAlign="center">
      <CircularProgress sx={{ color: '#18364a' }} />
      <Typography fontSize={15} color="#888" mt={2}>{t('view.loading')}</Typography>
    </Box>
  );
  if (error) return shell(<Typography fontSize={14} color="#b3261e">Firestore: {error}</Typography>);
  if (!derived) return shell(<Typography fontSize={15} color="#888">{t('view.noData')}</Typography>);

  const past = derived.stops.filter((s) => s.phase === 'PAST');
  const current = derived.stops.find((s) => s.phase === 'CURRENT') ?? null;
  const future = derived.stops.filter((s) => s.phase === 'FUTURE');

  const status = STATUS_STYLE[derived.status];
  const delay = delayLabel(derived.delaySec);
  const cov = derived.coverage;

  const routeLabel =
    `${derived.originName ?? ''}${derived.originSchedLabel ? ` (${derived.originSchedLabel})` : ''}`
    + ' \u2192 '
    + `${derived.destinationName ?? ''}${derived.destinationSchedLabel ? ` (${derived.destinationSchedLabel})` : ''}`;

  const stopProps = (s: DerivedStop) => ({
    station: s.name,
    time: '',
    delay: 0,
    occupancy: 0,                                   // level drives the icon, not this
    occupancyLevel: s.occupancy.level ?? undefined,
    occupancyState: s.occupancy.state,
    missingReason: s.occupancy.reason ? t(`reason.${s.occupancy.reason}`) : null,
    stateLabel:
      s.occupancy.state === 'FORECAST' ? t('occ.forecast')
      : s.occupancy.state === 'AWAITING' ? t('occ.awaiting')
      : s.occupancy.state === 'MISSING' ? t('occ.missing')
      : null,
    arrLabel: t('time.arr'),
    depLabel: t('time.dep'),
    arrivedLabel: t('terminus.arrived'),
    expectedArrivalLabel: t('terminus.expected'),
    passengers: s.occupancy.passengers ?? 0,
    cars: s.occupancy.cars,
    silhouette: s.occupancy.state === 'NONE',
    terminus: s.isTerminus,
    schedArrival: s.arrival.schedLabel,
    schedDeparture: s.departure.schedLabel,
    obsArrival: s.arrival.obsLabel,
    obsDeparture: s.departure.obsLabel,
    delayArrivalSec: s.arrival.delaySec,
    delayDepartureSec: s.departure.delaySec,
  });

  return (
    <>
      {topBar}

      <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: '0 auto', overflowY: 'hidden' }}>
        <Grid container spacing={2} sx={{ paddingTop: '28px' }}>
          <Grid size={3}>
            <Typography fontSize={18.5} fontWeight="bold" align="left">
              {derived.lineCode}
              <Box component="span" sx={{ ml: '8px', fontWeight: 400, fontSize: '15px', color: '#546e7a' }}>
                {derived.routeId ?? ''}
              </Box>
            </Typography>
          </Grid>
          <Grid size="grow">
            <Typography fontSize={16} align="right">{routeLabel}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '10px' }}>
          <Box component="span" sx={{
            px: '6px', py: '2px', fontSize: '11px', fontWeight: 600, borderRadius: '3px',
            color: status.fg, backgroundColor: status.bg,
          }}>
            {t(`status.${derived.status}`)}
          </Box>
          {delay && (
            <Typography fontSize={12} sx={{ color: '#d32f2f', fontWeight: 600 }}>
              {delay} min
            </Typography>
          )}
          {/* Coverage of the published VU output. missing > 0 is the signal the
              whole occupancy design exists to surface. */}
          <Typography fontSize={11} sx={{ color: cov.missing > 0 ? '#b3261e' : '#90a4ae' }}>
            {t('view.counts', { measured: cov.measured, called: cov.called })}
            {cov.awaiting > 0 && ` · ${t('view.awaiting', { n: cov.awaiting })}`}
            {cov.missing > 0 && ` · ${t('view.missing', { n: cov.missing })}`}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography fontSize={11} sx={{ color: '#b0bec5' }}>
            {t('view.stopCount', { n: derived.stops.length })}
          </Typography>
        </Box>

        <Box sx={{ overflow: 'auto', height: '760px', mt: '8px', pr: '4px' }}>
          {past.length > 0 && (
            <>
              <Box
                onClick={() => setShowPast((v) => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', mt: '6px', mb: '4px', userSelect: 'none',
                }}
              >
                <Typography fontSize={13} fontWeight="bold" sx={{ color: '#607d8b' }}>
                  {t('view.pastStops', { n: past.length })}
                </Typography>
                <Typography fontSize={11} sx={{ color: '#90a4ae' }}>
                  {showPast ? t('view.hide') : t('view.show')}
                </Typography>
              </Box>
              {showPast && past.map((s) => <StopRowPast key={s.seq} stop={s} />)}
            </>
          )}

          {current && (
            <Box ref={currentRef} sx={{
              border: '2px solid #4a7ba7', borderRadius: '3px',
              padding: '0px 10px 5px 6px', marginTop: '18px', backgroundColor: '#fbfdff',
            }}>
              <Typography fontSize={11} fontWeight="bold" sx={{ color: '#4a7ba7', pt: '6px' }}>
                {t('view.currentStop')}
              </Typography>
              <Train sx={{ paddingTop: '6px' }} {...stopProps(current)} />
              {/* Train itself carries no variance display, so without this the
                  figure would appear only once the train had moved on. */}
              {current.occupancy.state === 'MEASURED' && current.occupancy.errorVsH1?.passengers != null && (
                <Typography
                  fontSize={10}
                  sx={{ color: '#5c6bc0', pb: '4px' }}
                  title={
                    `${t('occ.delta')}`
                    + (current.occupancy.forecastH1?.train?.passengers != null
                        ? ` — ${t('occ.forecast')} ${current.occupancy.forecastH1.train.passengers}`
                        : '')
                    + (current.occupancy.errorVsH1?.model_version
                        ? ` · ${current.occupancy.errorVsH1.model_version}`
                        : '')
                  }
                >
                  {t('occ.variance')} Δ
                  {current.occupancy.errorVsH1.passengers > 0 ? '+' : ''}
                  {current.occupancy.errorVsH1.passengers}
                  {current.occupancy.accuracyPct !== null && (
                    <Box component="span" sx={{ ml: '3px', fontWeight: 700 }}>
                      ({current.occupancy.accuracyPct}%)
                    </Box>
                  )}
                </Typography>
              )}
            </Box>
          )}

          {future.length > 0 && (
            <Typography fontSize={13} fontWeight="bold" align="left" sx={{ marginTop: '26px', color: '#607d8b' }}>
              {derived.currentSeq === null ? t('view.route') : t('view.nextStops')}
            </Typography>
          )}
          {future.map((s) => (
            <Box key={s.seq} padding="0px 5px 0px 5px">
              <Train sx={{}} {...stopProps(s)} />
            </Box>
          ))}

          <Box sx={{ height: 80 }} />
        </Box>
      </Container>
    </>
  );
};

export default LiveView2;
